import hardcodedPronunciationLevelsGen from "#hardcoded-pronunciation-levels.gen.json" with { type: "json" };

/**
 * Curated pronunciation levels attached after a character level. Sourced from the published level
 * files (`@haniverse/levels`: `simplified/NNN-Pk/<order>/<glyph>.mdx` lesson YAML); rebuild with
 * `vpx _build-level-files` / the levels.files Moon `gen` task.
 *
 * Each entry becomes that level's `pronunciationCharacters` list (exact — nothing auto-appended).
 * The Browse label is `{afterLevel}-P{n}` where `n` is the 1-based index among every pronunciation
 * level in curriculum order (so after Level 1's P1, a list on Level 2 is `2-P2`).
 *
 * Readings are force-introduced only on the pronunciation lesson (`part: "pronunciation"`), never
 * on a meaning-only character lesson — so a gloss can be taught alone and its reading lands later.
 * `words` are the word lessons authored on that P-level (3-P3's 个子 / 这个): the trailing lessons on
 * that same authored level.
 */
export const HARDCODED_PRONUNCIATION_LEVELS = hardcodedPronunciationLevelsGen as readonly {
  afterLevel: number;
  readings: readonly { grapheme: string; pinyin: string }[];
  words?: readonly { definition: string; pinyin: string; word: string }[];
}[];

export type HardcodedPronunciationReading = {
  grapheme: string;
  pinyin: string;
};

export type HardcodedPronunciationWord = {
  definition: string;
  pinyin: string;
  word: string;
};

/** Level 1-P1 readings (the first hardcoded pronunciation level). */
export const HARDCODED_OPENING_PRONUNCIATIONS = HARDCODED_PRONUNCIATION_LEVELS[0]?.readings ?? [];

/** Level 1-P1 character slots (干 twice). */
export const HARDCODED_OPENING_PRONUNCIATION_CHARACTERS = HARDCODED_OPENING_PRONUNCIATIONS.map(
  ({ grapheme }) => grapheme,
);

/**
 * Every word authored on a pronunciation level file. They stay on that P-level, so
 * `_generate-levels` keeps them out of the HSK `N-Wk` batches.
 */
export const HARDCODED_PRONUNCIATION_LEVEL_WORD_FORMS: readonly string[] =
  HARDCODED_PRONUNCIATION_LEVELS.flatMap((level) => level.words?.map(({ word }) => word) ?? []);

/** Readings for the hardcoded pronunciation level attached after 1-based `afterLevel`, if any. */
export function hardcodedPronunciationReadingsAfter(
  afterLevel: number,
): readonly HardcodedPronunciationReading[] | undefined {
  return HARDCODED_PRONUNCIATION_LEVELS.find((level) => level.afterLevel === afterLevel)?.readings;
}

/** Trailing words for the hardcoded pronunciation level after 1-based `afterLevel`, if any. */
export function hardcodedPronunciationWordsAfter(
  afterLevel: number,
): readonly HardcodedPronunciationWord[] | undefined {
  return HARDCODED_PRONUNCIATION_LEVELS.find((level) => level.afterLevel === afterLevel)?.words;
}

/** Character slots (one per reading; a grapheme may repeat) for that P-level. */
export function hardcodedPronunciationCharactersAfter(
  afterLevel: number,
): readonly string[] | undefined {
  const readings = hardcodedPronunciationReadingsAfter(afterLevel);
  return readings?.map(({ grapheme }) => grapheme);
}

const hardcodedPronunciationKeySet = new Set(
  HARDCODED_PRONUNCIATION_LEVELS.flatMap(({ readings }) =>
    readings.map(({ grapheme, pinyin }) => `${grapheme}\0${pinyin}`),
  ),
);

/** Whether any hardcoded pronunciation level force-introduces this character reading. */
export function isHardcodedPronunciationReading({
  grapheme,
  pinyin,
}: {
  grapheme: string;
  pinyin: string;
}): boolean {
  return hardcodedPronunciationKeySet.has(`${grapheme}\0${pinyin}`);
}

/**
 * The hardcoded reading for pronunciation-level slot `slotIndex` on the P-level after 1-based
 * `afterLevel`, when that slot's grapheme matches. Undefined when that level has no hardcoded list,
 * the slot is past the list, or the grapheme disagrees.
 */
export function hardcodedPronunciationReadingAtSlot({
  afterLevel,
  grapheme,
  slotIndex,
}: {
  afterLevel: number;
  grapheme: string;
  slotIndex: number;
}): HardcodedPronunciationReading | undefined {
  const entry = hardcodedPronunciationReadingsAfter(afterLevel)?.[slotIndex];
  if (entry === undefined || entry.grapheme !== grapheme) {
    return undefined;
  }
  return entry;
}

/**
 * Order a grapheme's introduced readings for its lesson: hardcoded P-level readings first (in the
 * order they appear across authored P-levels — 干 gan4 before gan1), then any other introduced
 * readings in arrival order.
 */
export function orderIntroducedPinyinForGrapheme(
  grapheme: string,
  pinyinList: readonly string[],
): string[] {
  const available = new Set(pinyinList);
  const leading: string[] = HARDCODED_PRONUNCIATION_LEVELS.flatMap(({ readings }) =>
    readings.filter((entry) => entry.grapheme === grapheme && available.has(entry.pinyin)),
  ).map((entry) => entry.pinyin);
  if (leading.length === 0) {
    return [...pinyinList];
  }
  const leadingSet = new Set(leading);
  return [...leading, ...pinyinList.filter((pinyin) => !leadingSet.has(pinyin))];
}
